import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { PerfilService } from 'src/app/perfil/perfil.service';
import { RenegociacaoCartaoLoggerService } from 'src/app/renegociacao-cartao/renegociacao-cartao-logger.service';
import { RenegociacaoCartaoService } from 'src/app/renegociacao-cartao/renegociacao-cartao.service';
import { AutenticacaoService } from 'src/app/seguranca/autenticacao.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { CustomerDebtRenegociationData } from 'src/app/shared/models/customer-debt-renegociation';
import { LogAcaoCliente } from 'src/app/shared/models/log-acao-cliente-enum';
import { AcaoRenegociacaoCartaoLoggerEnum } from 'src/app/shared/models/renegociacao-cartao-logger-service.model';
import { ServicoCartaoIndisponivelError } from 'src/app/shared/models/servico-cartao-indisponivel.error';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { ToastService } from 'src/app/shared/toast';

type MostrarConteudo = {
  cartao: boolean;
  carne: boolean;
}

type MenuItem = {
  title: string;
  ctaText: string;
  icon: string;
  path: string;
  type: 'carne' | 'cartao';
};

@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.component.html',
  styleUrls: ['./home-page.component.scss']
})
export class HomePageComponent implements OnInit {
  isMinorThanTablet: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');
  dadosRenegociacaoCartao: CustomerDebtRenegociationData | null = null;
  LogAcaoCliente = LogAcaoCliente;
  loadingCartao = true;

  mostrarConteudo: MostrarConteudo = {
    carne: false,
    cartao: false,
  };

  menuItens: MenuItem[] = []

  private redirectMap = {
    [LogAcaoCliente.CARTAO_CASAS_BAHIA]: 'https://bit.ly/3DN7Buu',
    [LogAcaoCliente.COMPRA_CARNE]: 'https://www.casasbahia.com.br/hotsite/carne-digital.aspx?icid=hotbarcdc2024',
    [LogAcaoCliente.CENTRAL_ATENDIMENTO]: 'https://bit.ly/3vgTzxp',
  }

  constructor(
    private autenticacaoService: AutenticacaoService,
    private router: Router,
    public breakpointObserver: BreakpointObserver,
    private renegociacaoCartaoService: RenegociacaoCartaoService,
    public toast: ToastService,
    private loader: LoaderService,
    private renegociacaoCartaoLoggerService: RenegociacaoCartaoLoggerService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private perfilService: PerfilService
  ) { }

   ngOnInit(): void {
    this.handleGAPageEvent();
    this.addMeuCarne();
    this.addCartDebt();
  }

  async buscarDadosRenegociacaoCartao(): Promise<void> {
    let detail: string;

    try {
      this.loadingCartao = true;
      this.loader.start();

      const temPermissao = await this.renegociacaoCartaoService.verificarPermissaoRenegociacaoCartao();

      const resetarDadosRenegociacaoCartao = () => {
        this.dadosRenegociacaoCartao = null;
        this.renegociacaoCartaoService.dadosRenegociacaoCartao = null;
        this.mostrarConteudo.cartao = false;
        detail = this.autenticacaoService.possuiCarne 
          ? 'contrato de carnê encontrado, mas não há acordo de renegociação de cartãoo'
          : 'Contrato de carnê e acordo de renegociação de cartão não encontrados';
      }

      if (temPermissao) {
        this.dadosRenegociacaoCartao = await this.renegociacaoCartaoService.buscarDadosRenegociacaoCartao(true);
        if (this.dadosRenegociacaoCartao?.dividasCliente?.length > 0 || this.dadosRenegociacaoCartao?.acordosCliente?.length > 0) {
          this.renegociacaoCartaoService.dadosRenegociacaoCartao = this.dadosRenegociacaoCartao;
          detail = this.autenticacaoService.possuiCarne 
            ? 'contrato de carnê e acordo de renegociação de cartão encontrados'
            : 'acordo de renegociação de cartão encontrado, mas não há contrato de carnê';
          this.handleGAClickEvent(detail);
        } else {
          resetarDadosRenegociacaoCartao();
        }        
      } else {
        resetarDadosRenegociacaoCartao();
      }
    } catch (error) {
      if (error instanceof ServicoCartaoIndisponivelError) {
        this.toast.show({ type: 'error', title: 'Serviço de cartão indisponível.', text: 'Por favor, tente novamente mais tarde.' })
      } else {
        this.toast.show({ type: 'error', title: 'Erro ao buscar dados de renegociação de cartão.', text: 'Tente novamente mais tarde.' });
      }
      this.googleAnalyticsService.pushActionEvent({
        event: 'view_error',
        detail: error?.error?.message ?? 'Erro ao buscar dados de renegociação de cartão',
      })
    } finally {
      this.handleGAClickEvent(detail);
      this.loadingCartao = false;
      this.loader.stop();
    }
  }

  private async handleGAClickEvent(detail: string) {
    this.googleAnalyticsService.pushActionEvent({
      event: 'view',
      detail,
      context: 'portal reneg:consultar-carne-cartao',
    });
  }

  async enviarLogAcaoCliente(acaoCliente: LogAcaoCliente) {
    const gaDataByClientAction = {
      [LogAcaoCliente.CARTAO_CASAS_BAHIA]: 'solicitar cartao casas bahia',
      [LogAcaoCliente.COMPRA_CARNE]: 'comprar com carne digital',
      [LogAcaoCliente.CENTRAL_ATENDIMENTO]: 'central de atendimento',
    }

    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: gaDataByClientAction[acaoCliente],
      context: 'portal reneg:portal'
    })

    await this.autenticacaoService.salvarLogAcaoCliente(acaoCliente);
    window.open(this.redirectMap[acaoCliente], '_blank', 'noopener, noreferrer');
  }

  addMeuCarne(): void {
    if (this.autenticacaoService.possuiCarne) {
      this.menuItens.push({
        title: 'Serviços do carnê',
        ctaText: 'Acessar o meu carnê',
        icon: 'assets/images/icons/ic-cb-parcelamento.svg',
        path: '/portal/contratos',
        type: 'carne',
      });

      this.mostrarConteudo.carne = true;
    }
  }

  addCartDebt(): void {
    this.menuItens.push({
      title: 'Renegociação de cartão',
      ctaText: 'Acessar o meu cartão',
      icon: 'assets/images/icons/ic-cb-credit-card-menu.svg',
      path: '/portal/renegociacao/cartao/lista-cartoes',
      type: 'cartao',
    })

    this.mostrarConteudo.cartao = true;
  }

  voltarAosMeusServicos(): void {
    this.mostrarConteudo.cartao = true;

    if (this.autenticacaoService.possuiCarne) {
      this.mostrarConteudo.carne = true;
    }

    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'voltar aos meus serviços',
      context: 'portal reneg:portal',
    });
  }

  get username() {
    return this.autenticacaoService.autenticacao?.nomeCliente ?? '';
  }

  enviarLogAcesso(type: 'carne' | 'cartao') {
    const actionLogger = type === 'carne'
      ? AcaoRenegociacaoCartaoLoggerEnum.ACESSAR_CARNE_ACTION
      : AcaoRenegociacaoCartaoLoggerEnum.ACESSAR_CARTAO_BRADESCO_ACTION;

    this.renegociacaoCartaoLoggerService.enviarLogRenegociacaoCartao({ actionLogger });
  }

  async gotToMenuPath(menuItem: MenuItem) {
    if (menuItem.type === 'carne') {
      this.googleAnalyticsService.pushActionEvent({
        event: 'click',
        detail: 'acessar o meu carne',
        context: 'portal reneg:portal'
      })
      this.enviarLogAcesso(menuItem.type);
      this.router.navigate([menuItem.path]);
    } else if (menuItem.type === 'cartao') {
      this.googleAnalyticsService.pushActionEvent({
        event: 'click',
        detail: 'acessar o meu cartao',
        context: 'portal reneg:portal',
      })
      await this.buscarDadosRenegociacaoCartao();
      if (this.renegociacaoCartaoService.dadosRenegociacaoCartao) {
        this.enviarLogAcesso(menuItem.type);
        this.router.navigate([menuItem.path]);
      }
    }
  }

  get temNegociacaoCartaoDisponivel() {
    if (!this.dadosRenegociacaoCartao) {
      return false;
    }

    const debts = this.dadosRenegociacaoCartao?.dividasCliente?.flatMap(debt => debt.dividas) ?? [];
    return debts.some(debt => debt.ofertasNegociacao.length > 0);
  }

  async handleGAPageEvent() {

    this.googleAnalyticsService.pushPageViewEvent({
      page_name: '/renegocie/portal/servicos',
      page_template: 'renegocie'
    })

  }
}
